<template>
  <div class="flex h-full flex-col">
    <p class="mb-2 text-sm leading-relaxed text-gray-600 sm:mb-4 sm:text-base">
      Recomienda productos para mantener el valor del pedido.
    </p>
    <lokal-loading
      v-if="productsQueryIsLoading"
      size="xs"
      :loading="productsQueryIsLoading"
      class="flex-1"
    />
    <template v-else-if="products">
      <lokal-generic-combobox
        :model-value="selectedProducts"
        class="mb-2 sm:mb-4"
        :options="products"
        :get-option-label="(option) => option.name"
        :get-option-key="(option) => `${option.id}`"
        :placeholder="'Buscar producto'"
        :multiple="true"
        :options-label="'Recomendar productos'"
        @update:model-value="handleProductSelection"
      >
        <template
          v-for="product in products"
          :key="product.id"
          #[`custom-option-${product.id}`]="{ active, selected, option, getOptionLabel }"
        >
          <li
            class="flex cursor-pointer items-center space-x-2 p-1.5 transition-colors sm:space-x-2 sm:p-2"
            :class="{ 'bg-lk-green text-white': active || selected }"
          >
            <CheckIcon
              class="size-3 shrink-0 sm:size-4"
              :class="{ hidden: !selected, 'text-white': selected }"
            />
            <img
              :src="product.images?.[0]?.derivatives?.jpgSm?.url"
              class="size-6 shrink-0 rounded object-cover sm:size-8"
            />
            <div class="min-w-0 flex-1">
              <div class="truncate text-xs font-medium sm:text-sm">{{ getOptionLabel(option) }}</div>
              <div class="text-xs text-gray-400">{{ option.id }}</div>
            </div>
          </li>
        </template>
      </lokal-generic-combobox>
      <form
        id="recommendations-form"
        class="flex h-[350px] flex-col overflow-y-scroll md:max-h-[550px]"
        @submit.prevent="onSubmit"
      >
        <div
          v-if="selectedProducts.length > 0"
          class="flex-1 space-y-2 sm:space-y-2"
        >
          <h4 class="text-sm font-semibold sm:text-base">Productos recomendados:</h4>
          <div class="space-y-2 sm:space-y-2">
            <div
              v-for="product in selectedProducts"
              :key="product.id"
            >
              <div
                class="flex flex-col space-y-2 rounded-lg border border-green-200 bg-green-50/50 p-2 transition-all sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:p-3"
              >
                <div class="flex items-center space-x-2 sm:space-x-3">
                  <img
                    :src="product.images?.[0]?.derivatives?.jpgSm?.url"
                    class="size-8 rounded-lg object-cover shadow-sm sm:size-9"
                  />
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-xs font-semibold text-gray-900 sm:text-sm">{{ product.name }}</div>
                    <div class="text-xs text-gray-500">ID: {{ product.id }}</div>
                  </div>
                </div>
                <div class="flex items-center justify-between space-x-2 sm:justify-start sm:space-x-3">
                  <div class="flex items-center space-x-1.5 sm:space-x-2">
                    <label class="text-xs font-medium text-gray-700 sm:text-sm">Cantidad:</label>
                    <Field
                      :value="selectedProductQuantities[product.id]"
                      :name="`new-quantity-product-${product.id}`"
                      :label="`Cantidad de ${product.name}`"
                      type="number"
                      :rules="{ required: true, min_value: 1 }"
                      class="w-14 rounded-lg border p-1.5 text-center text-xs transition-all duration-200 focus:outline-none focus:ring-2 sm:w-16 sm:px-2 sm:text-sm"
                      :class="{
                        'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200':
                          !!errors[`new-quantity-product-${product.id}`],
                        'border-gray-300 bg-white focus:border-lk-green focus:ring-lk-green/20':
                          !errors[`new-quantity-product-${product.id}`],
                      }"
                      @input="updateQuantity(product.id, ($event.target as HTMLInputElement).value)"
                    />
                  </div>
                  <button
                    type="button"
                    class="rounded-lg p-1 text-red-500 transition-colors hover:bg-red-100 hover:text-red-700"
                    @click="removeProduct(product.id)"
                  >
                    <XMarkIcon class="size-3 sm:size-4" />
                  </button>
                </div>
              </div>
              <ErrorMessage
                :name="`new-quantity-product-${product.id}`"
                class="mt-1 text-sm text-red-600"
              />
            </div>
          </div>
        </div>
      </form>
      <div
        class="flex shrink-0 flex-col space-y-2 border-t border-gray-100 pt-3 sm:flex-row sm:justify-between sm:space-x-2 sm:space-y-0"
      >
        <button
          type="button"
          class="order-2 w-full text-sm text-gray-600 underline transition-colors hover:text-gray-800 sm:order-1 sm:w-auto sm:rounded-lg sm:border sm:border-gray-300 sm:bg-white sm:px-3 sm:py-2 sm:font-medium sm:text-gray-700 sm:no-underline sm:hover:bg-gray-50 sm:hover:text-gray-700"
          @click="emit('previous-step')"
        >
          Atrás
        </button>
        <button
          type="button"
          class="order-1 w-full rounded-lg border px-3 py-2 text-sm font-semibold transition-colors sm:order-2 sm:w-auto"
          :class="{
            'border-lk-green bg-white text-lk-green hover:bg-lk-green hover:text-white': !hasValidationErrors,
            'cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400': hasValidationErrors,
          }"
          :disabled="hasValidationErrors"
          @click="onSubmit"
        >
          Siguiente
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { CheckIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import { useForm, Field, ErrorMessage } from 'vee-validate';
import lokalGenericCombobox from './shared/lokal-generic-combobox.vue';
import lokalLoading from './shared/lokal-loading.vue';
import { computed } from 'vue';

interface SubOrderModificationsRecommendationsProps {
  products?: Product[];
  productsQueryIsLoading: boolean;
  selectedProducts: Product[];
  selectedProductQuantities: Record<string, number>;
}

interface SubOrderModificationsRecommendationsEmits {
  'selections-updated': [products: Product[], quantities: Record<string, number>];
  'previous-step': [];
  'next-step': [];
}

const props = defineProps<SubOrderModificationsRecommendationsProps>();
const emit = defineEmits<SubOrderModificationsRecommendationsEmits>();

const { handleSubmit, errors, setFieldValue } = useForm();

const hasValidationErrors = computed(() => Object.keys(errors.value).length > 0);

const onSubmit = handleSubmit(() => {
  emit('next-step');
});

function handleProductSelection(selectedProducts: Product | Product[]) {
  const products = Array.isArray(selectedProducts) ? selectedProducts : [selectedProducts];
  const newQuantities = { ...props.selectedProductQuantities };

  products.forEach((product) => {
    if (!(product.id in newQuantities)) {
      newQuantities[product.id] = 0;
      setFieldValue(`new-quantity-product-${product.id}`, 0);
    }
  });

  emit('selections-updated', products, newQuantities);
}

function removeProduct(productId: number) {
  const filteredProducts = props.selectedProducts.filter((product) => product.id !== productId);
  const newQuantities = { ...props.selectedProductQuantities };
  delete newQuantities[productId];
  emit('selections-updated', filteredProducts, newQuantities);
}

function updateQuantity(productId: number, quantity: string) {
  const newQuantities = { ...props.selectedProductQuantities };
  const numericQuantity = Number(quantity);
  newQuantities[productId] = numericQuantity;
  setFieldValue(`new-quantity-product-${productId}`, numericQuantity);
  emit('selections-updated', [...props.selectedProducts], newQuantities);
}
</script>
